import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Users, Trash2, Eye, TrendingUp, Palette, Building2 } from "lucide-react";

const COLORS = ["#3A1F6E", "#0D9488", "#D97706", "#DB2777", "#4F46E5", "#059669", "#DC2626", "#0EA5E9"];

export default function Workspace() {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [invite, setInvite] = useState({ email: "", role: "member" });

  const load = async () => {
    const { data } = await api.get("/workspaces");
    setList(data);
    if (data.length && !active) selectWs(data[0]);
    if (!data.length) setActive(null);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const selectWs = async (w) => {
    setActive(w);
    try { const { data } = await api.get(`/workspaces/${w.id}/analytics`); setAnalytics(data); } catch { setAnalytics(null); }
  };

  const createWs = async () => {
    try { const { data } = await api.post("/workspaces", { name: "New Workspace" }); toast.success("Workspace created"); await load(); selectWs(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveBrand = async (patch) => {
    const next = { ...active, ...patch };
    setActive(next);
    try { await api.put(`/workspaces/${active.id}`, { name: next.name, brand_color: next.brand_color, brand_logo: next.brand_logo || "" }); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addMember = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/workspaces/${active.id}/members`, invite); setActive((w) => ({ ...w, members: data })); setInvite({ email: "", role: "member" }); toast.success("Member added"); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const removeMember = async (uid) => {
    try { const { data } = await api.delete(`/workspaces/${active.id}/members/${uid}`); setActive((w) => ({ ...w, members: data })); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const del = async () => {
    try { await api.delete(`/workspaces/${active.id}`); toast.success("Deleted"); setActive(null); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <DashboardShell>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Workspace</h1>
          <p className="mt-2 text-muted-foreground">Bring your team together with shared members, a brand kit, and pooled analytics.</p>
        </div>
        <div className="flex items-center gap-2">
          {list?.length > 0 && (
            <Select value={active?.id || ""} onValueChange={(v) => selectWs(list.find((w) => w.id === v))}>
              <SelectTrigger className="w-48" data-testid="workspace-select"><SelectValue placeholder="Select workspace" /></SelectTrigger>
              <SelectContent>{list.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button onClick={createWs} className="rounded-full" data-testid="new-workspace-btn"><Plus size={16} className="mr-1" /> New</Button>
        </div>
      </div>

      {list === null ? null : !active ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-accent grid place-items-center text-primary"><Building2 size={26} /></div>
          <h3 className="font-display text-xl font-semibold text-foreground">No workspace yet</h3>
          <p className="mt-1 text-muted-foreground">Create a workspace for your school, team, museum, or company.</p>
          <Button onClick={createWs} className="mt-6 rounded-full" data-testid="empty-workspace-btn">Create workspace</Button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Brand + name */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4"><Palette size={13} /> Brand Kit</div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Workspace name</Label>
            <Input value={active.name} onChange={(e) => setActive((w) => ({ ...w, name: e.target.value }))} onBlur={(e) => saveBrand({ name: e.target.value })} data-testid="workspace-name" />
            <Label className="text-xs text-muted-foreground mb-1.5 block mt-4">Brand color</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => <button key={c} onClick={() => saveBrand({ brand_color: c })} className={`h-8 w-8 rounded-full transition-transform ${active.brand_color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`} style={{ background: c }} data-testid={`brand-${c}`} />)}
            </div>
            <div className="mt-5 rounded-xl h-16 grid place-items-center text-white font-display font-semibold" style={{ background: active.brand_color }}>{active.name}</div>
            {active.is_owner && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="mt-5 text-destructive" data-testid="delete-workspace-btn"><Trash2 size={14} className="mr-1" /> Delete workspace</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete “{active.name}”?</AlertDialogTitle><AlertDialogDescription>This removes the workspace and its members. Timelines are not affected.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-workspace">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          {/* Members */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4"><Users size={13} /> Members</div>
            {active.is_owner && (
              <form onSubmit={addMember} className="flex items-end gap-2 mb-4">
                <div className="flex-1"><Input type="email" required placeholder="teammate@example.com" value={invite.email} onChange={(e) => setInvite((s) => ({ ...s, email: e.target.value }))} data-testid="member-email" /></div>
                <Select value={invite.role} onValueChange={(v) => setInvite((s) => ({ ...s, role: v }))}>
                  <SelectTrigger className="w-28" data-testid="member-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="member">Member</SelectItem></SelectContent>
                </Select>
                <Button type="submit" size="sm" className="rounded-full" data-testid="add-member-btn">Add</Button>
              </form>
            )}
            <div className="space-y-2">
              <div className="flex items-center gap-3 rounded-lg border border-border p-2.5">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-primary-foreground text-xs">{(active.owner_name || "O").slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-foreground truncate">{active.owner_name}</div></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">owner</span>
              </div>
              {(active.members || []).map((m) => (
                <div key={m.user_id} className="flex items-center gap-3 rounded-lg border border-border p-2.5" data-testid={`member-${m.user_id}`}>
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-primary-foreground text-xs">{(m.name || "U").slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-foreground truncate">{m.name}</div><div className="text-xs text-muted-foreground truncate">{m.email}</div></div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{m.role}</span>
                  {active.is_owner && <button onClick={() => removeMember(m.user_id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-member-${m.user_id}`}><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </div>

          {/* Analytics */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4"><TrendingUp size={13} /> Workspace analytics</div>
            {analytics ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Members" value={analytics.member_count} />
                  <Stat label="Timelines" value={analytics.timeline_count} />
                  <Stat label="Published" value={analytics.published_count} />
                  <Stat label="Total views" value={analytics.total_views?.toLocaleString()} />
                </div>
                <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground mb-2">Top timelines</div>
                <div className="space-y-1.5">
                  {analytics.top?.length ? analytics.top.map((t) => (
                    <div key={t.slug} className="flex items-center justify-between text-sm"><span className="truncate text-foreground">{t.title}</span><span className="text-muted-foreground flex items-center gap-1 shrink-0"><Eye size={12} /> {t.views}</span></div>
                  )) : <p className="text-sm text-muted-foreground">No published timelines yet.</p>}
                </div>
              </>
            ) : <p className="text-sm text-muted-foreground">Loading…</p>}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl bg-muted p-3"><div className="font-display text-2xl font-semibold text-foreground">{value ?? 0}</div><div className="text-xs text-muted-foreground">{label}</div></div>
);
